
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Message, MessageContextType, MessageMetadata } from '@/types/insurance';
import { useChatMessages } from './useChatMessages';
import { useChatContextData } from './useChatContextData';
import { useChatMessageProcessing } from './useChatMessageProcessing';
import { messageContainsEmailOrTemplate } from '@/lib/utils';

export const useChatContext = (chatId?: string) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [currentChatId, setCurrentChatId] = useState<string | undefined>(chatId);
  const [emailContent, setEmailContent] = useState<string | null>(null);
  
  // Use our hooks but avoid circular dependency
  const contextData = useChatContextData(currentChatId);
  const messagesData = useChatMessages(currentChatId, contextData); // Pass contextData instead of creating a recursive import
  const messageProcessing = useChatMessageProcessing({ 
    chatId: currentChatId,
    messagesData,
    contextData
  });
  
  useEffect(() => {
    const initializeChat = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        const storedChatId = localStorage.getItem('currentChatId');
        console.log("Chat ID from props:", chatId, "Stored chat ID:", storedChatId);
        
        if (!currentChatId && storedChatId) {
          console.log("Using stored chat ID:", storedChatId);
          setCurrentChatId(storedChatId);
        }
        
        if (currentChatId) {
          console.log("Loading messages for chat ID:", currentChatId);
          // We need to make sure the loadMessages function exists in messagesData
          if (messagesData.loadMessages) {
            await messagesData.loadMessages();
          } else {
            console.warn("messagesData.loadMessages is not defined");
          }
          await contextData.loadContext();
          localStorage.setItem('currentChatId', currentChatId);
        }
      } catch (error) {
        console.error('Error initializing chat:', error);
      } finally {
        setLoading(false);
      }
    };
    
    initializeChat();
  }, [user, currentChatId]);
  
  // Handle email content state for concepts
  useEffect(() => {
    if (messagesData.messages) {
      const lastMessage = messagesData.messages[messagesData.messages.length - 1];
      if (lastMessage && messageContainsEmailOrTemplate(lastMessage.content)) {
        setEmailContent(lastMessage.content);
      }
    }
  }, [messagesData.messages]);

  // Handle explicit chatId changes from props
  useEffect(() => {
    if (chatId && chatId !== currentChatId) {
      console.log("Chat ID changed from props:", chatId);
      setCurrentChatId(chatId);
      localStorage.setItem('currentChatId', chatId);
      messagesData.setMessages([]);
    }
  }, [chatId, currentChatId]);
  
  // Listen for explicit reset events
  useEffect(() => {
    const handleResetChat = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.chatId) {
        console.log("Resetting chat to:", customEvent.detail.chatId);
        setCurrentChatId(customEvent.detail.chatId);
        messagesData.setMessages([]);
        // Make sure loadMessages exists before calling it
        if (messagesData.loadMessages) {
          messagesData.loadMessages();
        } else {
          console.warn("messagesData.loadMessages is not defined");
        }
      }
    };
    
    window.addEventListener('start-new-conversation', handleResetChat);
    window.addEventListener('open-chat', handleResetChat);
    
    return () => {
      window.removeEventListener('start-new-conversation', handleResetChat);
      window.removeEventListener('open-chat', handleResetChat);
    };
  }, []);
  
  useEffect(() => {
    if (!user) {
      localStorage.removeItem('currentChatId');
      setCurrentChatId(undefined);
      messagesData.setMessages([]);
    }
  }, [user]);
  
  return {
    chatId: currentChatId,
    messages: messagesData.messages,
    context: contextData.context,
    loading,
    addMessage: messagesData.addMessage,
    updateContext: contextData.updateContext,
    // Check if loadMessages exists
    loadMessages: messagesData.loadMessages || (() => Promise.resolve()),
    loadContext: contextData.loadContext,
    isStreaming: messagesData.setIsStreaming ? true : false,
    currentStreamedContent: messagesData.setCurrentStreamedContent ? '' : '',
    useOpenAI: contextData.useOpenAI,
    toggleModel: contextData.toggleModel,
    processMessage: messageProcessing.processMessage,
    threadId: contextData.threadId,
    emailContent
  };
};
